<template>
  <div class="flex min-w-0 flex-1 flex-col gap-3.5 rounded-xl bg-white p-8">
    <template v-if="status === 'submitted'">
      <div class="flex flex-col gap-4 sm:gap-1">
        <h4 class="text-[24px] leading-6 font-medium text-heading">Bedankt!</h4>
        <p class="text-base leading-[22px] font-light text-body">
          Je persoonlijke actieplan is verzonden naar het door jou opgegeven e-mailadres. Heb je vragen? Neem gerust
          contact op met onze collega's van Klantgeluk via
          <a
            href="mailto:info@pure-energie.nl"
            class="rounded-sm underline transition-colors hover:text-heading focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-bright"
            >info@pure-energie.nl</a
          >
          ☀️
        </p>
      </div>
      <button
        type="button"
        disabled
        class="h-12 w-full rounded-full bg-green/60 px-5 text-[18px] font-medium text-white"
      >
        Verzonden
      </button>
    </template>

    <template v-else>
      <!-- The design gives the heading more air on a phone than in the wide panel. -->
      <div class="flex flex-col gap-4 sm:gap-1">
        <h4 class="text-[24px] leading-6 font-medium text-heading">Ontdek wat jij nu kan doen</h4>
        <p class="text-base leading-[22px] font-light text-body">
          Op basis van jouw berekening heb ik een persoonlijk actieplan met concrete stappen gemaakt. Zo weet je precies
          hoe jij je energierekening kan verlagen in 2027.
        </p>
      </div>

      <form class="flex flex-col gap-3.5" novalidate @submit.prevent="submit">
        <div class="flex flex-col gap-1.5">
          <label for="action-plan-email" class="sr-only">E-mailadres</label>
          <input
            id="action-plan-email"
            v-model="email"
            type="email"
            autocomplete="email"
            placeholder="E-mailadres"
            class="h-12 w-full rounded-lg border border-line-strong px-4 text-base text-body placeholder:text-body/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-bright"
            :aria-describedby="message ? 'action-plan-message' : undefined"
            :aria-invalid="message !== '' || undefined"
          />
          <p v-if="message" id="action-plan-message" class="text-[14px] font-medium text-red-600">{{ message }}</p>
        </div>

        <button
          type="submit"
          class="h-12 w-full rounded-full px-5 text-[18px] font-medium text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-bright"
          :class="
            status === 'submitting' ? 'cursor-default bg-green/60' : 'cursor-pointer bg-green hover:bg-green-hover'
          "
          :disabled="status === 'submitting'"
        >
          {{ status === 'submitting' ? 'Versturen ...' : 'Ontvang gratis actieplan' }}
        </button>

        <p v-if="failureMessage" class="text-[14px] font-medium text-red-600">{{ failureMessage }}</p>

        <div class="flex flex-col gap-1.5">
          <CheckboxField id="newsletter-consent" v-model="newsletterConsent">
            Ja, ik ontvang graag mijn persoonlijke actieplan per e-mail en ik wil op de hoogte blijven.
          </CheckboxField>
          <p v-if="consentMessage" class="text-[14px] font-medium text-red-600">{{ consentMessage }}</p>
        </div>
      </form>
    </template>

    <!-- Tells screen readers how the request ended; visually the card content already changed. -->
    <p class="sr-only" role="status">{{ announcement }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { isCancelled, isThrottled, throttleMessage, validationErrors } from '@/api/client'
import CheckboxField from '@/components/form/CheckboxField.vue'
import { requestActionPlan } from '@/services/actionPlan'
import { trackLead } from '@/services/analytics'
import { useInputStore } from '@/stores/input'

/**
 * Local state on purpose: nothing else reads it, and "Opnieuw berekenen" unmounts the card, so a
 * new result starts with a fresh form for free.
 */
type Status = 'idle' | 'submitting' | 'submitted' | 'invalid' | 'throttled' | 'failed'

const input = useInputStore()

const email = ref('')
// Unchecked by default, deviating from the design: under the GDPR a pre-ticked box is not valid
// consent (decision on #6319). Ticking it is required to send the request (client decision on
// #6331); capi enforces that, like every other rule.
const newsletterConsent = ref(false)
const status = ref<Status>('idle')

/** The messages capi returned, keyed by its field names; the email one sits under the field. */
const errors = ref<Record<string, string>>({})

/** The consent message renders at the checkbox, so it stays out of the fallback under the field. */
const consentMessage = computed(() => errors.value.newsletter_consent ?? '')

const message = computed(() => {
  const { newsletter_consent: _, ...rest } = errors.value

  return errors.value.email ?? Object.values(rest)[0] ?? ''
})

/** What capi said about the throttling, empty when it sent nothing usable. */
const throttleNotice = ref('')

/**
 * The request did not go through. Against the limiter waiting is the only way forward, and capi
 * words that case itself: it is the only message that says so. Anything else, a 429 from a layer
 * that sends no body included, gets the line below.
 */
const failureMessage = computed(() => {
  if (status.value !== 'throttled' && status.value !== 'failed') {
    return ''
  }

  return throttleNotice.value || 'Het versturen is niet gelukt. Probeer het opnieuw.'
})

const announcement = computed(() => {
  if (status.value === 'submitted') {
    return 'Je aanvraag is verstuurd.'
  }

  return failureMessage.value
})

async function submit() {
  if (status.value === 'submitting') {
    return
  }

  errors.value = {}
  status.value = 'submitting'

  try {
    await requestActionPlan(input.toActionPlanInput(email.value, newsletterConsent.value))
    status.value = 'submitted'
    // The conversion: an address was left behind, but the event itself carries none (#6321).
    trackLead(input.contractType, newsletterConsent.value)
  } catch (error) {
    // A submit that replaces an earlier one aborts it; the newer call sets the status itself.
    if (isCancelled(error)) {
      return
    }

    // The limiter answers without field messages, so it is checked before them.
    if (isThrottled(error)) {
      throttleNotice.value = throttleMessage(error)
      status.value = 'throttled'

      return
    }

    // capi checks the address; anything it names lands under the field it belongs to.
    errors.value = validationErrors(error)
    status.value = Object.keys(errors.value).length > 0 ? 'invalid' : 'failed'
  }
}

// Editing the field or ticking the box answers its message, so the message leaves with the edit.
watch([email, newsletterConsent], () => (errors.value = {}))
</script>
